import type {
  ListingsAPIResponseFacet,
  ListingsAPIResponseNumericFacetValue,
  ListingsAPIResponseTextFacetValue,
} from '@noths/polaris-client-listings-api';

import type {
  FacetConfig,
  TextFacetConfig,
} from 'src/adapters/configToStore/listings-api/listingsControlsConfig.types';
import type { Facet, FacetOption } from 'src/redux/productsQuery/types/Facet';
import { logger } from 'src/utils/serverLogger';
import { getUpdatedOptionsForNumericFacet } from './getUpdatedOptionsForNumericFacet';
import { getUpdatedOptionsForTextFacet } from './getUpdatedOptionsForTextFacet';

const mapNumericOptions = (values: ListingsAPIResponseNumericFacetValue[]): FacetOption[] => {
  return values.map((value) => {
    const { maximum: max = null, minimum: min = null } = value.interval || {};

    return {
      active: false,
      count: parseInt(value.count, 10),
      title: `${min ? min : ''} - ${max ? max : ''}`,
      value: `${min ? min : ''}-${max ? max : ''}`,
    };
  });
};

const mapTextOptions = (
  facetConfig: TextFacetConfig,
  values: ListingsAPIResponseTextFacetValue[],
): FacetOption[] => {
  if (facetConfig.allowedValues) {
    return values
      .map((value) => {
        const textValue = value.value;
        const allowedValue = facetConfig.allowedValues?.find((av) => av.value === textValue);

        return allowedValue
          ? {
              active: false,
              count: parseInt(value.count, 10),
              title: allowedValue.title,
              value: textValue,
            }
          : null;
      })
      .filter(Boolean) as FacetOption[];
  }

  return values.map((value) => ({
    active: false,
    count: parseInt(value.count, 10),
    title: value.value,
    value: value.value,
  }));
};

export const adaptResponseFacetsToStore = ({
  facetConfig,
  listingsFacets,
  removeZeroCountFacets,
  storeFacets,
}: {
  facetConfig: FacetConfig[];
  listingsFacets: ListingsAPIResponseFacet[];
  removeZeroCountFacets?: boolean;
  storeFacets: Facet[];
}): Facet[] => {
  const updatedStoreFacets = storeFacets.reduce((facetsAcc, storeFacet) => {
    const listingsFacet = listingsFacets.find((facet) => facet.key === storeFacet.key);

    if (!listingsFacet) {
      facetsAcc.push(storeFacet);

      return facetsAcc;
    }

    let options: FacetOption[] = [];

    if (storeFacet.valueType === 'numeric') {
      options = getUpdatedOptionsForNumericFacet(
        storeFacet,
        listingsFacet.values as ListingsAPIResponseNumericFacetValue[],
      );
    }

    if (storeFacet.valueType === 'text') {
      options = getUpdatedOptionsForTextFacet(
        storeFacet,
        listingsFacet.values as ListingsAPIResponseTextFacetValue[],
      );
    }

    const allOptionsHaveZeroCount = options.every((option) => option.count === 0);

    if (removeZeroCountFacets && allOptionsHaveZeroCount) {
      return facetsAcc;
    }

    facetsAcc.push({
      ...storeFacet,
      options,
    });

    return facetsAcc;
  }, [] as Facet[]);

  listingsFacets.forEach((listingsFacet) => {
    const existingFacet = updatedStoreFacets.find((facet) => facet.key === listingsFacet.key);
    if (existingFacet) {
      return;
    }

    const facetConfigEntry = facetConfig.find((config) => config.key === listingsFacet.key);

    if (!facetConfigEntry) {
      logger.warn(
        `dynamic facet received for attribute "${listingsFacet.key}" from listings-api but no matching facet config found`,
      );
      return;
    }

    if (facetConfigEntry.visibility === 'never') {
      return;
    }

    const firstValue = listingsFacet.values?.[0];

    if (!firstValue) {
      return;
    }

    const facetIsNumeric = 'interval' in firstValue;
    let options: FacetOption[] = [];

    if (facetIsNumeric) {
      options = mapNumericOptions(listingsFacet.values as ListingsAPIResponseNumericFacetValue[]);
    } else {
      options = mapTextOptions(
        facetConfigEntry as TextFacetConfig,
        listingsFacet.values as ListingsAPIResponseTextFacetValue[],
      );
    }

    if (options.length) {
      updatedStoreFacets.push({
        key: listingsFacet.key,
        options,
        title: facetConfigEntry.title,
        type: facetConfigEntry.uiType || 'multi',
        valueType: facetIsNumeric ? 'numeric' : 'text',
        ...('allowedValues' in facetConfigEntry ? { disallowNewValues: true } : {}),
        ...(facetConfigEntry.singleOptionOnly === true ? { singleOptionOnly: true } : {}),
      });
    }
  });

  return updatedStoreFacets;
};
